import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Stack,
  Typography,
  Paper,
  Button,
  Chip,
  CircularProgress,
  Skeleton,
  LinearProgress,
  Collapse,
} from '@mui/material';
import {
  Mail,
  RotateCw,
  CheckCircle,
  AlertCircle,
  ChevronDown,
  FileText,
} from 'lucide-react';
import { toast } from '../../../../../ui/toast';
import { useSendCCAInviteMutation } from '../../../../../utils/api_service/ccaApi';
import { CCAQuestionResult, CCAStatus } from '../../../../../utils/types/cca_types';
import { Applicant } from '../../../../../utils/types/JobOpenings';
import { CCA_SUBDOMAINS } from '../../../../questions/CCABank/ccaConstants';
import { useCCAApplicantResult } from '../Hooks/UseCCAApplicantResult';

const toDisplayName = (s: string) =>
  s.includes('_')
    ? s
      .split('_')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ')
    : s;

const scoreColor = (score: number) => {
  if (score >= 80) return '#059669';
  if (score >= 60) return '#d97706';
  return '#dc2626';
};

type StatusKey = 'completed' | 'in-progress' | 'invite-sent' | 'other';

const mapStatusKey = (status: CCAStatus): StatusKey => {
  if (status === 'Completed') return 'completed';
  if (status === 'In Progress') return 'in-progress';
  if (status === 'Invite Sent') return 'invite-sent';
  return 'other';
};

const statusConfig: Record<
  StatusKey,
  { label: string; color: string; bgColor: string; icon: React.ReactNode }
> = {
  'invite-sent': {
    label: 'Invite Sent',
    color: '#1d4ed8',
    bgColor: '#eff6ff',
    icon: <Mail size={16} />,
  },
  'in-progress': {
    label: 'In Progress',
    color: '#b45309',
    bgColor: '#fffbeb',
    icon: <RotateCw size={16} />,
  },
  completed: {
    label: 'Completed',
    color: '#047857',
    bgColor: '#ecfdf5',
    icon: <CheckCircle size={16} />,
  },
  other: {
    label: 'Not Invited',
    color: '#475569',
    bgColor: '#f1f5f9',
    icon: <AlertCircle size={16} />,
  },
};

const subdomainColors: Record<string, { bar: string; badge: string; color: string }> = {
  'Abstract Reasoning': {
    bar: '#8b5cf6',
    badge: '#f5f3ff',
    color: '#6d28d9',
  },
  'Logical Reasoning': {
    bar: '#3b82f6',
    badge: '#eff6ff',
    color: '#1d4ed8',
  },
  'Quantitative Aptitude': {
    bar: '#10b981',
    badge: '#ecfdf5',
    color: '#047857',
  },
  'Verbal Ability': {
    bar: '#f59e0b',
    badge: '#fffbeb',
    color: '#b45309',
  },
  'Data Interpretation': {
    bar: '#f43f5e',
    badge: '#fff1f2',
    color: '#be123c',
  },
};

const getSubdomainColors = (subdomain: string) =>
  subdomainColors[toDisplayName(subdomain)] ?? {
    bar: '#94a3b8',
    badge: '#f1f5f9',
    color: '#475569',
  };

export interface CCAResultsTabProps {
  candidate: Applicant;
}

export const CCAResultsTab: React.FC<CCAResultsTabProps> = ({ candidate }) => {
  const { id: jobOpeningId = '' } = useParams<{ id: string }>();
  const { result, subdomainScores, isLoading, isError } = useCCAApplicantResult(
    candidate.userId,
  );

  const [expandedQuestion, setExpandedQuestion] = useState<string | null>(null);
  const [sendInviteMutation, { isLoading: isSending }] = useSendCCAInviteMutation();

  const handleSendInvite = async () => {
    if (!jobOpeningId || !candidate.userId) return;

    try {
      await sendInviteMutation({ jobOpeningId, applicantIds: [candidate.userId] }).unwrap();
      toast.success('CCA invite sent successfully.', {
        position: 'bottom-center',
      });
    } catch {
      toast.error('Failed to send CCA invite. Please try again.', {
        position: 'bottom-center',
      });
    }
  };

  if (isLoading) {
    return (
      <Stack spacing={2}>
        <Skeleton variant='rounded' height={112} sx={{ borderRadius: 2 }} />
        <Stack spacing={1}>
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} variant='rounded' height={64} sx={{ borderRadius: 1.5 }} />
          ))}
        </Stack>
      </Stack>
    );
  }

  if (isError || !result) {
    return (
      <Paper
        variant='outlined'
        sx={{
          p: 6,
          textAlign: 'center',
          bgcolor: 'action.hover',
          borderRadius: 3,
          borderStyle: 'dashed',
          m: 3,
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            bgcolor: 'background.paper',
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 3,
            boxShadow: 1,
          }}
        >
          <FileText size={40} style={{ color: '#cbd5e1' }} />
        </Box>
        <Typography variant='h6' sx={{ fontWeight: 700, mb: 1 }}>
          No CCA data available
        </Typography>
        <Typography variant='body2' sx={{ color: 'text.secondary', maxWidth: 280, mx: 'auto', mb: 4 }}>
          {isError
            ? 'Failed to load result. You can send a CCA invite to start the assessment.'
            : 'This candidate has not been invited to the CCA yet.'}
        </Typography>

        <Button
          variant='contained'
          color='success'
          onClick={handleSendInvite}
          disabled={isSending || !jobOpeningId}
          startIcon={isSending ? <CircularProgress size={16} color='inherit' /> : <Mail size={16} />}
          sx={{
            px: 3,
            py: 1.25,
            borderRadius: 2,
            fontWeight: 700,
            textTransform: 'none',
          }}
        >
          {isSending ? 'Sending Invitation...' : 'Send CCA Invite'}
        </Button>
      </Paper>
    );
  }

  const statusKey = mapStatusKey(result.assessment.status);
  const sc = statusConfig[statusKey];
  const isCompleted = statusKey === 'completed';

  const subdomainOrder: string[] = (CCA_SUBDOMAINS as string[]).length
    ? (CCA_SUBDOMAINS as string[])
    : Object.keys(subdomainScores);
  const hasResultData =
    Object.keys(subdomainScores).length > 0 ||
    (result.responses && result.responses.length > 0) ||
    result.assessment.totalScore != null;

  return (
    <Stack spacing={3}>
      {/* Header card */}
      <Paper
        variant='outlined'
        sx={{
          p: 3,
          borderRadius: 2,
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
          <Box>
            <Typography variant='subtitle1' sx={{ fontWeight: 700 }}>
              Cognitive Capability Assessment
            </Typography>
            <Typography variant='caption' sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}>
              5 Subdomains &bull; 10 Levels &bull; Video-enabled
            </Typography>
          </Box>
          <Chip
            icon={sc.icon as React.ReactElement}
            label={sc.label}
            sx={{
              fontWeight: 700,
              bgcolor: sc.bgColor,
              color: sc.color,
              border: '1px solid',
              borderColor: `${sc.color}40`,
            }}
          />
        </Box>

        {isCompleted && result.assessment.totalScore != null && (
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 3 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant='h4' sx={{ fontWeight: 900 }}>
                <Box component='span' sx={{ color: scoreColor(result.assessment.totalScore) }}>
                  {result.assessment.totalScore}
                </Box>
                <Box component='span' sx={{ fontSize: '1.125rem', fontWeight: 600, color: 'text.secondary' }}>
                  /100
                </Box>
              </Typography>
              <Typography variant='caption' sx={{ color: 'text.secondary', mt: 0.25, display: 'block' }}>
                Overall Score
              </Typography>
            </Box>
            {result.assessment.completedAt && (
              <Box sx={{ flex: 1, textAlign: 'right' }}>
                <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                  Completed
                </Typography>
                <Typography variant='body2' sx={{ fontWeight: 600 }}>
                  {new Date(result.assessment.completedAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                  })}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {statusKey === 'invite-sent' && (
          <Typography variant='body2' sx={{ mt: 1.5, color: 'text.secondary' }}>
            The assessment invite has been sent. Awaiting the candidate to begin.
          </Typography>
        )}
        {statusKey === 'in-progress' && (
          <Typography variant='body2' sx={{ mt: 1.5, color: 'warning.dark' }}>
            The candidate has started the assessment. Results will appear once completed.
          </Typography>
        )}
        {statusKey === 'other' && (
          <Typography variant='body2' sx={{ mt: 1.5, color: 'text.secondary' }}>
            This candidate has not yet been invited to take the CCA.
          </Typography>
        )}
      </Paper>

      {isCompleted && !hasResultData && (
        <Paper
          variant='outlined'
          sx={{
            p: 6,
            textAlign: 'center',
            bgcolor: 'action.hover',
            borderRadius: 3,
            borderStyle: 'dashed',
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              bgcolor: 'background.paper',
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2.5,
              boxShadow: 1,
            }}
          >
            <FileText size={32} style={{ color: '#cbd5e1' }} />
          </Box>
          <Typography variant='subtitle1' sx={{ fontWeight: 700, mb: 1 }}>
            No CCA results available
          </Typography>
          <Typography variant='body2' sx={{ color: 'text.secondary', maxWidth: 300, mx: 'auto' }}>
            The CCA assessment record exists, but no score or question results
            are available for this candidate yet.
          </Typography>
        </Paper>
      )}

      {/* Subdomain scores */}
      {isCompleted && Object.keys(subdomainScores).length > 0 && (
        <Box>
          <Typography
            variant='caption'
            sx={{
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: 1,
              color: 'text.secondary',
              display: 'block',
              mb: 1.5,
            }}
          >
            Subdomain Breakdown
          </Typography>
          <Stack spacing={1.5}>
            {subdomainOrder.map((sd) => {
              const score =
                subdomainScores[sd] ??
                subdomainScores[sd.toLowerCase().replace(/ /g, '_')] ??
                0;
              const colors = getSubdomainColors(sd);
              return (
                <Paper
                  key={sd}
                  variant='outlined'
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Chip
                      size='small'
                      label={toDisplayName(sd)}
                      sx={{
                        fontWeight: 700,
                        fontSize: '0.6875rem',
                        bgcolor: colors.badge,
                        color: colors.color,
                        border: '1px solid',
                        borderColor: `${colors.color}40`,
                      }}
                    />
                    <Typography variant='body2' sx={{ fontWeight: 700, color: scoreColor(score) }}>
                      {score}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant='determinate'
                    value={score}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: 'action.hover',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: colors.bar,
                        borderRadius: 4,
                      },
                    }}
                  />
                </Paper>
              );
            })}
          </Stack>
        </Box>
      )}

      {/* Per-question results */}
      {isCompleted && result.responses && result.responses.length > 0 && (
        <Box>
          <Typography
            variant='caption'
            sx={{
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: 1,
              color: 'text.secondary',
              display: 'block',
              mb: 1.5,
            }}
          >
            Question Results ({result.responses.length})
          </Typography>
          <Stack spacing={1}>
            {result.responses.map((qr: CCAQuestionResult, i: number) => {
              const isExpanded = expandedQuestion === qr.questionId;
              const correct =
                qr.isCorrect ?? (qr.aiScore != null ? qr.aiScore >= 80 : false);
              const questionText = qr.questionSnapshot.question.text ?? '(image question)';
              const candidateAnswer =
                qr.shortAnswerText ??
                (qr.questionSnapshot.options.find((o) => o.id === qr.selectedOptionId)?.text ?? null);

              return (
                <Paper
                  key={qr.questionId}
                  variant='outlined'
                  sx={{
                    borderRadius: 2,
                    overflow: 'hidden',
                    bgcolor: 'background.paper',
                  }}
                >
                  <Box
                    component='button'
                    type='button'
                    onClick={() => setExpandedQuestion(isExpanded ? null : qr.questionId)}
                    sx={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      p: 2,
                      textAlign: 'left',
                      bgcolor: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.625rem',
                        fontWeight: 700,
                        bgcolor: correct ? 'success.light' : 'error.light',
                        color: correct ? 'success.dark' : 'error.dark',
                        flexShrink: 0,
                      }}
                    >
                      {i + 1}
                    </Box>
                    <Typography
                      variant='body2'
                      sx={{
                        flex: 1,
                        color: 'text.primary',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {questionText}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                      {qr.aiScore != null && (
                        <Typography variant='caption' sx={{ fontWeight: 700, color: scoreColor(qr.aiScore) }}>
                          {qr.aiScore}%
                        </Typography>
                      )}
                      <ChevronDown
                        size={16}
                        style={{
                          color: '#94a3b8',
                          transform: isExpanded ? 'rotate(180deg)' : 'none',
                          transition: 'transform 0.2s',
                        }}
                      />
                    </Box>
                  </Box>

                  <Collapse in={isExpanded}>
                    <Box sx={{ p: 2, pt: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>
                      {candidateAnswer && (
                        <Box sx={{ mb: 1.5 }}>
                          <Typography variant='caption' sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                            Candidate Answer
                          </Typography>
                          <Paper
                            elevation={0}
                            sx={{
                              p: 1.5,
                              borderRadius: 1,
                              fontSize: '0.875rem',
                              fontWeight: 500,
                              bgcolor: correct ? 'success.light' : 'error.light',
                              color: correct ? 'success.dark' : 'error.dark',
                            }}
                          >
                            {candidateAnswer}
                          </Paper>
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                          {toDisplayName(qr.subdomain)}
                        </Typography>
                        <Typography variant='caption' sx={{ color: 'text.disabled' }}>&bull;</Typography>
                        <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                          Level {qr.questionSnapshot.level}
                        </Typography>
                        <Typography variant='caption' sx={{ color: 'text.disabled' }}>&bull;</Typography>
                        <Typography variant='caption' sx={{ color: 'text.secondary', textTransform: 'capitalize' }}>
                          {qr.questionType.replace('_', ' ')}
                        </Typography>
                      </Box>
                    </Box>
                  </Collapse>
                </Paper>
              );
            })}
          </Stack>
        </Box>
      )}
    </Stack>
  );
};

export default CCAResultsTab;
